import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import type { PatientSignedNote } from "../api/clinicalWorkspace";

/** Read-only note snapshots live only in this signed-in browser session. */
export interface SignedNoteTab {
  /** Session-only routing identity, independent of patient and encounter IDs. */
  readonly id: string;
  readonly patientId: string;
  readonly label: string;
  readonly note: PatientSignedNote;
}

interface SignedNoteTabsContextValue {
  readonly tabs: readonly SignedNoteTab[];
  openSignedNote(input: Omit<SignedNoteTab, "id">): string;
  closeSignedNote(id: string): void;
  clearSignedNotes(): void;
}

const SignedNoteTabsContext = createContext<SignedNoteTabsContextValue | null>(null);

export function SignedNoteTabsProvider({ children }: { readonly children: ReactNode }) {
  const [tabs, setTabs] = useState<readonly SignedNoteTab[]>([]);
  const tabsRef = useRef(tabs);

  const openSignedNote = useCallback<SignedNoteTabsContextValue["openSignedNote"]>((input) => {
    const existing = tabsRef.current.find(
      (tab) => tab.patientId === input.patientId && tab.note.encounterId === input.note.encounterId,
    );
    const id = existing?.id ?? crypto.randomUUID();
    // A stale history request must not replace a newer amendment already viewed.
    const note =
      existing && existing.note.noteVersion > input.note.noteVersion ? existing.note : input.note;
    const next = { ...input, id, note };
    const updated = existing
      ? tabsRef.current.map((tab) => (tab.id === id ? next : tab))
      : [...tabsRef.current, next];
    tabsRef.current = updated;
    setTabs(updated);
    return id;
  }, []);

  const closeSignedNote = useCallback((id: string) => {
    const updated = tabsRef.current.filter((tab) => tab.id !== id);
    tabsRef.current = updated;
    setTabs(updated);
  }, []);

  const clearSignedNotes = useCallback(() => {
    tabsRef.current = [];
    setTabs([]);
  }, []);

  const value = useMemo(
    () => ({ tabs, openSignedNote, closeSignedNote, clearSignedNotes }),
    [tabs, openSignedNote, closeSignedNote, clearSignedNotes],
  );
  return <SignedNoteTabsContext.Provider value={value}>{children}</SignedNoteTabsContext.Provider>;
}

export function useSignedNoteTabs(): SignedNoteTabsContextValue {
  const context = useContext(SignedNoteTabsContext);
  if (!context) throw new Error("useSignedNoteTabs must be used inside SignedNoteTabsProvider");
  return context;
}
